<template>
  <b-container
    v-if="show"
    class="p-0 justify-content-center align-items-center infocard"
    :class="classes"
    :style="infoCardStyle"
  >
    <b-row
      class="h-100 overflow-auto"
      no-gutters
      align-v="stretch"
      align-h="center"
      :cols-sm="cols('sm')"
      :cols-md="cols('md')"
      :cols-lg="cols('lg')"
      :cols-xl="cols('xl')"
    >
      <InfoCardContentPanel
        v-for="panel in contentPanels"
        :key="panel.key"
        :content-panel="panel"
        :fill="isFill"
        :style="contentPanelsStyle"
      />
    </b-row>
  </b-container>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { color, RGBColor } from 'd3-color'

import InfoCardContentPanel from './InfoCardContentPanel.vue'
import { CustomTabNode, InfoCardSizes } from '@/types'
import { DEFAULT_INFO_CARD_OPACITY, DEFAULT_INFO_CARD_COLOUR } from '@/constants'

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl'

const components = {
  InfoCardContentPanel,
}

const props = {
  tabNode: Object as () => CustomTabNode | undefined,
}

@Component({ components })
export default class InfoCard extends Vue.extend({ props }) {
  get show() {
    return this.tabNode?.config.showInfoCard
  }

  get isFill() {
    return this.tabNode?.config.infoCardSize === InfoCardSizes.FILL
  }

  get classes() {
    const isAuto = !this.isFill
    return {
      fill: this.isFill,
      'm-sm-0': isAuto,
      'm-md-4': isAuto,
      'm-lg-0': isAuto,
      'mw-lg-75': isAuto,
      'm-xl-5': isAuto,
      'mw-xl-55': isAuto,
    }
  }

  get infoCardBackgroundColour() {
    const colour = (color(this.tabNode?.config.infoCardColour ?? DEFAULT_INFO_CARD_COLOUR) ??
      color(DEFAULT_INFO_CARD_COLOUR)) as RGBColor
    colour.opacity = this.tabNode?.config.infoCardOpacity ?? DEFAULT_INFO_CARD_OPACITY

    return colour.formatRgb()
  }

  get infoCardStyle() {
    return {
      background: this.infoCardBackgroundColour,
    }
  }

  get contentPanelsColour() {
    return this.tabNode?.config.infoCardContentPanelsColour
  }

  get contentPanelsOpacity() {
    return this.tabNode?.config.infoCardContentPanelsOpacity
  }

  get contentPanelsBackgroundColour() {
    if (this.contentPanelsColour || this.contentPanelsOpacity) {
      const colour = color(this.contentPanelsColour ?? DEFAULT_INFO_CARD_COLOUR) as RGBColor
      colour.opacity = this.contentPanelsOpacity ?? 0

      return colour.formatRgb()
    } else {
      return 'transparent'
    }
  }

  get contentPanelsStyle() {
    return {
      background: this.contentPanelsBackgroundColour,
    }
  }

  get contentPanels() {
    return this.tabNode?.config?.infoCardContentPanels ?? []
  }

  get contentPanelsLength() {
    return this.contentPanels.length
  }

  cols(breakpoint: Breakpoint) {
    let maxPanels
    switch (breakpoint) {
      case 'sm':
      case 'md':
        maxPanels = 1
        break
      case 'lg':
        maxPanels = 2
        break
      case 'xl':
        // One special case where 4 content panels sits nicely in 2 columns, any more and we spread over 3
        maxPanels = this.contentPanelsLength === 4 ? 2 : 3
        break
    }

    return Math.min(this.contentPanelsLength, maxPanels)
  }
}
</script>

<style scoped>
.infocard {
  border-radius: var(--border-radius);
  .card {
    border-radius: var(--border-radius);
  }
}

.fill {
  height: 100%;
  min-height: 100%;
  width: 100%;
  min-width: 100%;
  border-radius: 0;
}
</style>
